import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { CONFIG } from '@frontend/config';
import { useWS } from '@frontend/features/common/hooks/useWS';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { createTravelServicesUpdateHandlers } from './useTravelServicesUpdateHandlers';

const AGENCY_ROOM = (agencyId: number) => `travel_services_agency_${agencyId}`;

/**
 * Novedades del worker de sync (confirmaciones asíncronas, tickets emitidos,
 * holds por vencer): refresca el listado y avisa al agente. El room es por
 * agencia y el server autoriza el join contra el JWT.
 */
export const useTravelServicesUpdates = (agencyId?: number) => {
  const { isConnected, socketRef, joinRoom, leaveRoom } = useWS(
    `${CONFIG.WS_URL}/travel-services`,
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isConnected || !agencyId) return;
    const room = AGENCY_ROOM(agencyId);
    joinRoom(room);
    return () => leaveRoom(room);
  }, [isConnected, agencyId, joinRoom, leaveRoom]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !agencyId) return;

    const { onUpdated, onNotice, onExpiring } = createTravelServicesUpdateHandlers({
      queryClient,
      addToast,
      navigate,
      t,
    });

    socket.on('travel:booking:updated', onUpdated);
    socket.on('travel:booking:notice', onNotice);
    socket.on('travel:booking:holdExpiring', onExpiring);
    return () => {
      socket.off('travel:booking:updated', onUpdated);
      socket.off('travel:booking:notice', onNotice);
      socket.off('travel:booking:holdExpiring', onExpiring);
    };
  }, [socketRef, isConnected, agencyId, queryClient, addToast, t, navigate]);
};
